import React, { useEffect, useMemo, useState } from "react";
import Api from "../../Services/Api";
import { useNavigate } from "react-router-dom";

const formatBRL = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/**
 * Converte string "1.234,56" -> 1234.56
 */
const brlStringToNumber = (value) => {
  if (!value) return 0;
  const cleaned = value
    .toString()
    .replace(/\s/g, "")
    .replace(/[R$]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Formata digitando: "123456" -> "1.234,56"
 */
const numberToBRLTyping = (digitsOnly) => {
  const digits = (digitsOnly || "").replace(/\D/g, "");
  if (!digits) return "";
  const int = Number(digits) / 100;
  return int.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const splitBranch = (branchValue = "") => {
  const raw = String(branchValue || "").trim();
  if (!raw) return { agency: "", agency_digit: "", branch_display: "-" };

  const [agencyPart, agencyDigitPart] = raw.split("-");
  const agency = String(agencyPart || "").trim();
  const agency_digit = String(agencyDigitPart || "").trim();

  if (!agency) return { agency: "", agency_digit: "", branch_display: "-" };

  return {
    agency,
    agency_digit,
    branch_display: agency_digit ? `${agency}-${agency_digit}` : agency,
  };
};

const CardSolicitarSaque = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const [balance, setBalance] = useState({
    saldo_total_disponivel: 0,
    creditos_confirmados: 0,
    limite_saque: 0,
  });

  const [conta, setConta] = useState(null);

  const [valorStr, setValorStr] = useState("");
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  const maxPermitido = Number(balance?.saldo_total_disponivel || 0);
  const valorNum = useMemo(() => brlStringToNumber(valorStr), [valorStr]);

  const contaFormatada = useMemo(() => {
    if (!conta?.account_number) return "-";
    if (conta?.account_digit) return `${conta.account_number}-${conta.account_digit}`;
    return conta.account_number;
  }, [conta]);

  const bancoNome = useMemo(() => {
    if (!conta) return "";
    if (conta.bank_name) return conta.bank_name;
    if (conta.bank_code) return `Banco ${conta.bank_code}`;
    return "Nao informado";
  }, [conta]);

  const agenciaInfo = useMemo(() => {
    const branch = conta?.branch || "";
    if (branch) return splitBranch(branch);

    const agency = String(conta?.agency || "").trim();
    const agency_digit = String(conta?.agency_digit || "").trim();
    if (!agency) return { agency: "", agency_digit: "", branch_display: "-" };

    return {
      agency,
      agency_digit,
      branch_display: agency_digit ? `${agency}-${agency_digit}` : agency,
    };
  }, [conta]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);

        const [balRes, bankRes] = await Promise.all([
          Api.get("/ledger-entries/me/balance"),
          Api.get("/bank-accounts/me"),
        ]);

        const bal = balRes.data?.data ?? balRes.data ?? {};
        const bank = bankRes.data?.data ?? bankRes.data ?? null;

        setBalance(bal);
        setConta(bank);
      } catch (e) {
        console.error(e);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const validar = () => {
    setErro("");
    setSucesso("");

    if (!conta) return "Voce precisa cadastrar seus dados bancarios antes de solicitar saque.";
    if (maxPermitido <= 0) return "Seu saldo disponivel para saque esta zerado no momento.";
    if (!valorNum || valorNum <= 0) return "Informe um valor valido para saque.";
    if (valorNum > maxPermitido) {
      return `O valor solicitado nao pode ser maior que o saldo disponivel (${formatBRL(maxPermitido)}).`;
    }

    return "";
  };

  const onUsarMaximo = () => {
    const s = Number(maxPermitido).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    setValorStr(s);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const msg = validar();
    if (msg) {
      setErro(msg);
      return;
    }

    try {
      setLoadingSubmit(true);
      setErro("");
      setSucesso("");

      await Api.post("/withdraw-requests", {
        amount: valorNum,
        bank_account: {
          id: conta?.id || null,
          bank_name: conta.bank_name,
          bank_code: conta.bank_code,
          branch: conta.branch || agenciaInfo.branch_display,
          agency: agenciaInfo.agency,
          agency_digit: agenciaInfo.agency_digit,
          account_number: conta.account_number,
          account_digit: conta.account_digit,
          pix_key: conta.pix_key,
          account_holder_name: conta.account_holder_name || conta.holder_name,
          account_holder_cpf_cnpj: conta.account_holder_cpf_cnpj || conta.holder_document,
          holder_name: conta.account_holder_name || conta.holder_name,
          holder_document: conta.account_holder_cpf_cnpj || conta.holder_document,
          account_type: conta.account_type,
        },
      });

      navigate("/dashboard", {
        state: { toast: "Solicitacao enviada com sucesso!" },
        replace: true,
      });
    } catch (e) {
      console.log("STATUS:", e?.response?.status);
      console.log("DATA:", e?.response?.data);
      setErro(e?.response?.data?.message || "Erro ao solicitar saque");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const disabledForm = loading || loadingSubmit || !conta || maxPermitido <= 0;

  return (
    <article className="card">
      <div className="card-header">
        <div className="card-title">
          <span>🏦</span>
          <span>Solicitar saque</span>
        </div>
      </div>

      <div className="card-body">
        {loading ? (
          <div>Carregando informacoes...</div>
        ) : (
          <>
            <div className="withdraw-top">
              <div className="withdraw-metrics">
                <div>
                  <strong>Saldo disponivel:</strong> {formatBRL(balance?.saldo_total_disponivel)}
                </div>
                <div>
                  <strong>Limite disponivel para saque:</strong> {formatBRL(balance?.limite_saque)}
                </div>
              </div>
            </div>

            <div className="withdraw-bank">
              {!conta ? (
                <>
                  <div>
                    <strong>Banco:</strong> Nao informado
                  </div>
                  <div>
                    <strong>Agencia:</strong> -
                  </div>
                  <div>
                    <strong>Conta:</strong> -
                  </div>
                  <div className="card-sub">
                    Voce ainda nao cadastrou seus dados. Preencha na tela de <strong>Dados bancarios</strong>.
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <strong>Banco:</strong> {bancoNome}
                  </div>
                  <div>
                    <strong>Agencia:</strong> {agenciaInfo.branch_display}
                  </div>
                  <div>
                    <strong>Conta:</strong> {contaFormatada}
                  </div>
                  {conta?.pix_key ? (
                    <div>
                      <strong>Pix:</strong> {conta.pix_key}
                    </div>
                  ) : null}
                  <div className="card-sub">
                    Alteracoes devem ser feitas na tela de <strong>Dados bancarios</strong> no menu lateral.
                  </div>
                </>
              )}
            </div>

            {erro ? <div className="alert alert-error">{erro}</div> : null}
            {sucesso ? <div className="alert alert-success">{sucesso}</div> : null}

            <form onSubmit={onSubmit} className="withdraw-form">
              <label className="withdraw-label">
                <strong>Valor do saque</strong>
              </label>

              <div className="withdraw-row">
                <input
                  value={valorStr}
                  onChange={(e) => {
                    setErro("");
                    setSucesso("");
                    setValorStr(numberToBRLTyping(e.target.value));
                  }}
                  placeholder="0,00"
                  inputMode="numeric"
                  className="input withdraw-input"
                  disabled={disabledForm}
                />

                <button
                  type="button"
                  className="secondary-btn"
                  onClick={onUsarMaximo}
                  disabled={disabledForm}
                >
                  Usar maximo
                </button>
              </div>

              <div className="withdraw-max">
                Maximo permitido: <strong>{formatBRL(maxPermitido)}</strong>
              </div>

              <div className="withdraw-actions">
                <button type="submit" className="primary-btn" disabled={disabledForm}>
                  {loadingSubmit ? "Enviando..." : "Confirmar solicitacao"}
                </button>

                <a href="/dadosbancarios" className="inline-link">
                  Ir para dados bancarios
                </a>
              </div>
            </form>
          </>
        )}
      </div>
    </article>
  );
};

export default CardSolicitarSaque;
